import { useCallback, useMemo, useRef, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useAppToast } from '../ui/useAppToast';
import { apiService } from '../../services/api.service';
import { mergeArtifacts } from '../../services/api/artifacts.api';
import { ActionToast } from '../../components/ui/ActionToast';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
// Note: EventBus removed - entity changes are now broadcast via WebSocket by the backend
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type {
  BulkOperationResult,
  UndoMoveData,
  UndoCopyData,
  UndoData,
} from '../../types/bulkOperations';

/** Shape of meta returned by move endpoints */
interface MoveResponseMeta {
  originalParentType?: 'workspace' | 'project';
  originalParentId?: string | null;
}

/** Shape of data returned by copy/create endpoints */
interface CopyResponseData {
  id?: string;
  parentEntityType?: string;
  parentEntityId?: string;
}

/**
 * Configuration for bulk operations
 */
export interface BulkOperationsConfig<T> {
  /** Array of items to operate on */
  items: T[];
  /** Function to extract ID from an item */
  getItemId: (item: T) => string;
  /** Function to determine if items have mixed types (disables move/copy) */
  hasMixedTypesCheck?: (selectedItems: T[]) => boolean;
  /** Callback to refresh data after operations */
  onRefresh?: () => void | Promise<void>;
  /** Entity type for the operations (e.g., 'projects', 'artifacts') */
  entityType: 'projects' | 'artifacts' | 'mixed';
}

// Re-export types for convenience
export type { BulkOperationResult, UndoMoveData, UndoCopyData, UndoData };

/**
 * Custom hook for managing bulk operations (delete, move, copy)
 * Provides state management, handlers, and UI state for bulk actions
 */
export function useBulkOperations<T>(config: BulkOperationsConfig<T>) {
  const { items, getItemId, hasMixedTypesCheck, onRefresh, entityType } = config;
  const toast = useAppToast();
  const { workspace: _workspace } = useWorkspace();

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Undo data state
  const [lastUndoData, setLastUndoData] = useState<UndoData | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Bulk delete state
  const {
    isOpen: isBulkDeleteOpen,
    onOpen: onBulkDeleteOpen,
    onClose: onBulkDeleteClose,
  } = useDisclosure();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Bulk merge state
  const {
    isOpen: isMergeDialogOpen,
    onOpen: onMergeDialogOpen,
    onClose: onMergeDialogClose,
  } = useDisclosure();
  const [isMerging, setIsMerging] = useState(false);

  // Bulk move/copy state
  const {
    isOpen: isBulkParentSelectorOpen,
    onOpen: onBulkParentSelectorOpen,
    onClose: onBulkParentSelectorClose,
  } = useDisclosure();
  const [bulkOperationType, setBulkOperationType] = useState<'move' | 'copy'>('move');
  const bulkToolbarRef = useRef<HTMLDivElement>(null);
  const moveButtonRef = useRef<HTMLButtonElement>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  // Check if selected items have mixed types
  const hasMixedTypes = useMemo(() => {
    if (selectedIds.length === 0 || !hasMixedTypesCheck) return false;
    const selectedItems = items.filter((item) => selectedIds.includes(getItemId(item)));
    return hasMixedTypesCheck(selectedItems);
  }, [selectedIds, items, getItemId, hasMixedTypesCheck]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Undo handler for move operations - accepts move data as parameter
  const handleUndoMove = useCallback(
    async (moveData: UndoMoveData[]) => {
      try {
        let succeeded = 0;
        let failed = 0;

        for (const moveItem of moveData) {
          try {
            const endpoint =
              moveItem.itemType === 'project'
                ? `/collections/${moveItem.itemId}/move`
                : `/artifacts/${moveItem.itemId}/move`;

            const payload = {
              targetParentType: moveItem.originalParentType,
              targetParentId: moveItem.originalParentId,
            };

            const response = await apiService.post(endpoint, payload);

            if (response.success) {
              succeeded++;
            } else {
              failed++;
            }
          } catch (error) {
            failed++;
          }
        }

        // Refresh data
        if (onRefresh) {
          await onRefresh();
        }

        // Show result toast
        if (failed === 0) {
          toast({
            title: 'Undo Successful',
            description: `Moved ${succeeded} item${succeeded !== 1 ? 's' : ''} back to original location`,
            status: 'success',
          });
        } else {
          toast({
            title: 'Undo Partially Completed',
            description: `Moved ${succeeded} of ${moveData.length} items back. ${failed} failed.`,
            status: 'warning',
          });
        }
      } catch (error: unknown) {
        toast({
          title: 'Undo Failed',
          description: error instanceof Error ? error.message : 'Failed to undo move operation',
          status: 'error',
        });
      }
    },
    [onRefresh, toast],
  );

  // Undo handler for copy operations - accepts copy data as parameter
  const handleUndoCopy = useCallback(
    async (copyData: UndoCopyData[]) => {
      try {
        let succeeded = 0;
        let failed = 0;

        for (const copyItem of copyData) {
          try {
            const endpoint =
              copyItem.itemType === 'project'
                ? `/collections/${copyItem.copiedItemId}`
                : `/artifacts/${copyItem.copiedItemId}`;

            const response = await apiService.delete(endpoint);

            if (response.success) {
              succeeded++;
            } else {
              failed++;
            }
          } catch (error) {
            failed++;
          }
        }

        // Refresh data
        if (onRefresh) {
          await onRefresh();
        }

        // Show result toast
        if (failed === 0) {
          toast({
            title: 'Undo Successful',
            description: `Deleted ${succeeded} copied item${succeeded !== 1 ? 's' : ''}`,
            status: 'success',
          });
        } else {
          toast({
            title: 'Undo Partially Completed',
            description: `Deleted ${succeeded} of ${copyData.length} items. ${failed} failed.`,
            status: 'warning',
          });
        }
      } catch (error: unknown) {
        toast({
          title: 'Undo Failed',
          description: error instanceof Error ? error.message : 'Failed to undo copy operation',
          status: 'error',
        });
      }
    },
    [onRefresh, toast],
  );

  // Universal undo handler that accepts undo data as parameter (to avoid closure issues)
  const handleUndoWithData = useCallback(
    async (undoData: UndoData) => {
      if (undoData.operation === 'move' && undoData.moveData) {
        await handleUndoMove(undoData.moveData);
      } else if (undoData.operation === 'copy' && undoData.copyData) {
        await handleUndoCopy(undoData.copyData);
      }

      // Clear undo data after execution
      setLastUndoData(null);
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    },
    [handleUndoMove, handleUndoCopy],
  );

  // Universal undo handler that delegates to appropriate handler (uses lastUndoData from state)
  const handleUndo = useCallback(async () => {
    if (!lastUndoData) {
      return;
    }

    await handleUndoWithData(lastUndoData);
  }, [lastUndoData, handleUndoWithData]);

  // Bulk move/copy handler for single entity type (projects or artifacts)
  const handleBulkMoveOrCopySingleType = useCallback(
    async (
      parentType: 'workspace' | 'project',
      parentId: string,
      operation: 'move' | 'copy',
    ): Promise<{ result: BulkOperationResult; undoData: UndoData }> => {
      let succeeded = 0;
      let failed = 0;
      const moveData: UndoMoveData[] = [];
      const copyData: UndoCopyData[] = [];

      const itemType: 'project' | 'artifact' = entityType === 'projects' ? 'project' : 'artifact';

      for (const itemId of selectedIds) {
        try {
          const endpoint =
            entityType === 'projects'
              ? `/collections/${itemId}/${operation}`
              : `/artifacts/${itemId}/${operation}`;

          const payload = {
            targetParentType: parentType,
            targetParentId: parentId,
          };

          const response = await apiService.post(endpoint, payload);

          if (response.success) {
            succeeded++;

            // Note: Entity changes are broadcast via WebSocket by the backend

            // Capture undo data based on operation type
            if (operation === 'move' && response.meta) {
              const meta = response.meta as unknown as MoveResponseMeta;
              if (meta.originalParentType) {
                moveData.push({
                  itemId,
                  itemType,
                  originalParentType: meta.originalParentType,
                  originalParentId: meta.originalParentId || null,
                });
              }
            } else if (operation === 'copy' && response.data) {
              const data = response.data as unknown as CopyResponseData;
              copyData.push({
                copiedItemId: data.id ?? '',
                itemType,
              });
            }
          } else {
            failed++;
          }
        } catch (error) {
          failed++;
        }
      }

      return {
        result: { succeeded, failed, total: selectedIds.length },
        undoData: {
          operation,
          moveData: operation === 'move' ? moveData : undefined,
          copyData: operation === 'copy' ? copyData : undefined,
        },
      };
    },
    [selectedIds, entityType],
  );

  // Bulk move/copy handler for mixed entity types (used in ProjectDetail with projects, artifacts)
  const handleBulkMoveOrCopyMixed = useCallback(
    async (
      parentType: 'workspace' | 'project',
      parentId: string,
      operation: 'move' | 'copy',
      itemsByType: {
        projects?: Array<{ id: string }>;
        artifacts?: Array<{ id: string }>;
      },
    ): Promise<{ result: BulkOperationResult; undoData: UndoData }> => {
      let succeeded = 0;
      let failed = 0;
      const moveData: UndoMoveData[] = [];
      const copyData: UndoCopyData[] = [];

      // Process sub-projects
      if (itemsByType.projects) {
        for (const project of itemsByType.projects) {
          try {
            const response = await apiService.post(`/collections/${project.id}/${operation}`, {
              targetParentType: parentType,
              targetParentId: parentId,
            });

            if (response.success) {
              succeeded++;

              // Note: Entity changes are broadcast via WebSocket by the backend

              // Capture undo data
              if (operation === 'move' && response.meta) {
                const meta = response.meta as unknown as MoveResponseMeta;
                if (meta.originalParentType) {
                  moveData.push({
                    itemId: project.id,
                    itemType: 'project',
                    originalParentType: meta.originalParentType,
                    originalParentId: meta.originalParentId || null,
                  });
                }
              } else if (operation === 'copy' && response.data) {
                const data = response.data as unknown as CopyResponseData;
                if (data.id) {
                  copyData.push({
                    copiedItemId: data.id,
                    itemType: 'project',
                  });

                  // Track project creation from copy
                  safeAnalytics.track(ANALYTICS_EVENTS.COLLECTION_CREATED, {
                    creation_source: 'bulk_operations',
                    creation_method: 'copy',
                    creation_screen: 'collection_detail',
                    creation_trigger: 'bulk_action',
                    project_id: data.id,
                    source_project_id: project.id,
                    parent_type: parentType,
                    parent_id: parentId,
                  });
                  safeAnalytics.track(ANALYTICS_EVENTS.CONTENT_CREATED, {
                    content_type: 'collection',
                    creation_method: 'copy',
                  });
                }
              }
            } else {
              failed++;
            }
          } catch (error) {
            failed++;
          }
        }
      }

      // Process artifacts
      if (itemsByType.artifacts) {
        for (const artifact of itemsByType.artifacts) {
          try {
            const response = await apiService.post(`/artifacts/${artifact.id}/${operation}`, {
              targetParentType: parentType,
              targetParentId: parentId,
            });

            if (response.success) {
              succeeded++;

              // Note: Entity changes are broadcast via WebSocket by the backend

              // Capture undo data
              if (operation === 'move' && response.meta) {
                const meta = response.meta as unknown as MoveResponseMeta;
                if (meta.originalParentType) {
                  moveData.push({
                    itemId: artifact.id,
                    itemType: 'artifact',
                    originalParentType: meta.originalParentType,
                    originalParentId: meta.originalParentId || null,
                  });
                }
              } else if (operation === 'copy' && response.data) {
                const data = response.data as unknown as CopyResponseData;
                if (data.id) {
                  copyData.push({
                    copiedItemId: data.id,
                    itemType: 'artifact',
                  });

                  safeAnalytics.track(ANALYTICS_EVENTS.CONTENT_CREATED, {
                    content_type: 'note',
                    creation_method: 'copy',
                  });
                }
              }
            } else {
              failed++;
            }
          } catch (error) {
            failed++;
          }
        }
      }

      const total = (itemsByType.projects?.length || 0) + (itemsByType.artifacts?.length || 0);

      return {
        result: { succeeded, failed, total },
        undoData: {
          operation,
          moveData: operation === 'move' ? moveData : undefined,
          copyData: operation === 'copy' ? copyData : undefined,
        },
      };
    },
    [],
  );

  // Main bulk move/copy handler
  const handleBulkMoveOrCopy = useCallback(
    async (
      parentType: 'workspace' | 'project',
      parentId: string,
      itemsByType?: {
        projects?: Array<{ id: string }>;
        artifacts?: Array<{ id: string }>;
      },
      /**
       * Opt-in for a caller with no picker (the workspace canvas paste): run
       * `operation` on `itemsByType` without a selection, and hear the result.
       * Every other caller leaves it out and keeps the picker's operation.
       */
      options?: {
        readonly operation: 'move' | 'copy';
        readonly onResult?: (result: BulkOperationResult) => void;
      },
    ) => {
      if (!options && selectedIds.length === 0) return;

      try {
        const operation = options?.operation ?? bulkOperationType;

        // Use appropriate handler based on entity type
        const { result, undoData } =
          entityType === 'mixed' && itemsByType
            ? await handleBulkMoveOrCopyMixed(parentType, parentId, operation, itemsByType)
            : await handleBulkMoveOrCopySingleType(parentType, parentId, operation);
        options?.onResult?.(result);

        // Store undo data if there were successful operations
        if (result.succeeded > 0) {
          // Clear any existing undo timeout
          if (undoTimeoutRef.current) {
            clearTimeout(undoTimeoutRef.current);
          }

          // Set undo data
          setLastUndoData(undoData);

          // Clear undo data after 10 seconds (toast duration)
          undoTimeoutRef.current = setTimeout(() => {
            setLastUndoData(null);
          }, 10000);
        }

        // Clear selection
        clearSelection();

        // Refresh data
        if (onRefresh) {
          await onRefresh();
        }

        // Show toast with results and undo button
        const toastStatus = result.failed === 0 ? ('success' as const) : ('warning' as const);
        const toastTitle =
          result.failed === 0
            ? `Items ${operation === 'move' ? 'Moved' : 'Copied'}`
            : `Bulk ${operation === 'move' ? 'Move' : 'Copy'} Completed`;
        const toastDescription =
          result.failed === 0
            ? `Successfully ${operation === 'move' ? 'moved' : 'copied'} ${result.succeeded} item${result.succeeded !== 1 ? 's' : ''}`
            : `${operation === 'move' ? 'Moved' : 'Copied'} ${result.succeeded} of ${result.total} items. ${result.failed} failed.`;

        const renderToast = (props: { onClose: () => void }) => {
          return (
            <ActionToast
              title={toastTitle}
              description={toastDescription}
              status={toastStatus}
              actionLabel="Undo"
              onAction={() => handleUndoWithData(undoData)}
              onClose={props.onClose}
            />
          );
        };

        toast({
          duration: 10000, // Extended to allow time for undo
          isClosable: true,
          render: renderToast,
        });
      } catch (error: unknown) {
        toast({
          title: 'Error',
          description:
            error instanceof Error
              ? error.message
              : `Failed to ${options?.operation ?? bulkOperationType} items`,
          status: 'error',
        });
      }
    },
    [
      selectedIds,
      bulkOperationType,
      entityType,
      handleBulkMoveOrCopySingleType,
      handleBulkMoveOrCopyMixed,
      clearSelection,
      onRefresh,
      toast,
      handleUndoWithData,
    ],
  );

  // Handlers for opening move/copy modals
  const openBulkMove = useCallback(() => {
    setBulkOperationType('move');
    onBulkParentSelectorOpen();
  }, [onBulkParentSelectorOpen]);

  const openBulkCopy = useCallback(() => {
    setBulkOperationType('copy');
    onBulkParentSelectorOpen();
  }, [onBulkParentSelectorOpen]);

  // Merge handler
  const handleBulkMerge = useCallback(async () => {
    if (selectedIds.length < 2) return;

    setIsMerging(true);
    try {
      const response = await mergeArtifacts(apiService, selectedIds);

      if (response.success) {
        const data = response.data as {
          survivingArtifactId: string;
          mergedCount: number;
          attachmentsMoved: number;
        };

        safeAnalytics.track(ANALYTICS_EVENTS.NOTES_MERGED, {
          note_count: data.mergedCount,
          surviving_note_id: data.survivingArtifactId,
          had_attachments: data.attachmentsMoved > 0,
          total_attachments_moved: data.attachmentsMoved,
        });

        toast({
          title: `Merged ${data.mergedCount} notes`,
          status: 'success',
          duration: 5000,
        });

        clearSelection();
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        // Surface the server's reason whenever it gave one. `api.service`
        // RESOLVES 4xx envelopes rather than throwing, so every deliberate
        // merge refusal lands HERE, not in the catch below — and all three are
        // things the user must act on: no EDIT on a participant, a partial
        // re-parent, and MERGE_WOULD_BROADEN_ACCESS (a selected note is
        // visible to fewer people than the note that would survive). "Please
        // try again" is wrong for all three, because retrying cannot help; for
        // the privacy refusal it also hides the only way forward.
        toast({
          title: 'Merge failed',
          description:
            response.error?.message ?? 'Your notes have not been changed. Please try again.',
          status: 'error',
          duration: 8000,
        });
      }
    } catch (error: unknown) {
      toast({
        title: 'Merge failed',
        description:
          error instanceof Error
            ? error.message
            : 'Your notes have not been changed. Please try again.',
        status: 'error',
        duration: 8000,
      });
    } finally {
      setIsMerging(false);
      onMergeDialogClose();
    }
  }, [selectedIds, clearSelection, onRefresh, toast, onMergeDialogClose]);

  // Get the active button ref based on operation type
  const activeButtonRef = bulkOperationType === 'move' ? moveButtonRef : copyButtonRef;

  return {
    // Selection state
    selectedIds,
    setSelectedIds,
    clearSelection,

    // Mixed types check
    hasMixedTypes,

    // Delete state
    isBulkDeleteOpen,
    onBulkDeleteOpen,
    onBulkDeleteClose,
    isBulkDeleting,
    setIsBulkDeleting,

    // Merge state
    isMergeDialogOpen,
    onMergeDialogOpen,
    onMergeDialogClose,
    isMerging,
    handleBulkMerge,

    // Move/Copy state
    isBulkParentSelectorOpen,
    onBulkParentSelectorClose,
    bulkOperationType,
    bulkToolbarRef,
    moveButtonRef,
    copyButtonRef,
    activeButtonRef,

    // Undo state and handler
    lastUndoData,
    handleUndo,

    // Handlers
    handleBulkMoveOrCopy,
    openBulkMove,
    openBulkCopy,
  };
}
